import React, { useCallback, useContext, useEffect } from 'react';
import ResultsVehicle from '../components/ResultsVehicle';
import { styled, Text, TokenTextAppearance, TokenTextColor, TextTag } from '@volkswagen-onehub/components-core';
import NoResultsVehicle from './NoResultsVehicle';
import { useLocation, useParams } from 'react-router-dom';
import { Route } from '../utils/use-route';
import { getLang, getTranslations } from '../translations';
import { replaceStringWithJSX } from '../utils/replace-string-with-jsx';
import { formatDistance } from '../utils/format';
import { useFeatureServices } from '../use-feature-services';
import { createConstantData, createPartner } from '../utils/get-tracking-event-data';
import { getSingleSelectedDealer } from '../utils/get-single-selected-dealer';
import { getSelectedDealers } from '../utils/get-selected-dealers';
import useRootHistory from '../utils/use-root-history';
import { UserLocationContext } from '../UserLocation/UserLocationContext';
import { useAppliedFilterStore } from '../store/filtersv2';
import { getFilterList } from '../utils/get-filter-list';
import { DealerData, VehicleData, VehiclesResponse } from '../types';
import { useVehiclesStore } from '../utils/use-vehicles-store';
import { useDealersStore } from '../utils/use-dealers-store';
import useVehiclesQuery from '../api/use-vehicles-query';

const VehicleResultsContainer = styled.div`
  margin: 0px ${props => props.theme.size.grid002};
  max-width: 1750px;
`;

export default () => {

  useRootHistory();

	const { allDealers: all_dealers, localDealer } = useDealersStore();
  const { applied } = useAppliedFilterStore();
  const selectedDealers = applied.dealer as string[];
  const page = applied.page;

  const { data } = useVehiclesQuery<VehiclesResponse, VehiclesResponse>();
  const { dealerVehicles, regularVehicles, vehicleSetsReady } = useVehiclesStore();

	const locationCtx = useContext(UserLocationContext);
	const location = useLocation();
  const { labels, error_messages: { no_dealer_stock, no_stock } } = getTranslations();
  const lang = getLang();

  const params = useParams();

  const { tracking } = useFeatureServices();
  const partner = getSingleSelectedDealer();

  // useVehicleResultsPaginate();

	useEffect(() => {
		// if(dealerVehicles?.length === 0 || regularVehicles?.length === 0 ) return;
    if (localDealer && vehicleSetsReady) {
      if (!dealerVehicles?.length && regularVehicles?.length) {
        tracking.track('VWBasic_InfoMessage_Load', {
          ...createConstantData(),
            partner: createPartner(localDealer),
            link: {
              url: window.location.href,
              name: "Search results page",
              newTab: false,
              isDealerPage: false
            },
            error: {
              message: no_dealer_stock.subhead.replace("%%localDealer%%", localDealer.name),
              referringUrl: window.location.href
            }
        });
      } else if (!dealerVehicles?.length && !regularVehicles?.length) {
        tracking.track('VWBasic_VehicleErrorMessage_Load', {
          ...createConstantData(),
          partner: createPartner(localDealer),
          link: {
            url: window.location.href,
            name: "Search results page",
            newTab: false,
            isDealerPage: false
          },
          error: {
            message: no_stock.subhead,
            referringUrl: window.location.href
          }
        })
      } else {
        tracking.track('VWBasic_FilterSearchResult_Load', {
          ...createConstantData(),
          partner: createPartner(partner),
          filter: {
            type: "complex",
            name: "Vehicle search",
            numberOfResults: data?.meta?.pagination?.total,
            filterlist: [
              {
                type: "multi_value",
                name: "DID",
                values: [getSelectedDealers(selectedDealers, all_dealers as DealerData[], 'dealer_id')]
              },
              ...getFilterList(applied, true)
            ]
          }
        });
      }
    }
	}, [dealerVehicles, regularVehicles, localDealer, vehicleSetsReady]);

  const showNoVehiclesMessage = useCallback(() => {
    if (location.pathname.includes(Route.RESULTS) && page === 1 && !params.ulid && !dealerVehicles?.length) {
      const zeroResults = !dealerVehicles?.length && !regularVehicles?.length;
      return <NoResultsVehicle isLocalDealer={!zeroResults} />;
    }

    return null;
  }, [dealerVehicles, regularVehicles]);

	return  (
			<VehicleResultsContainer>

        { !dealerVehicles?.length && (!selectedDealers.length || selectedDealers.includes(localDealer.static_dealer_id)) ? showNoVehiclesMessage() : null }

        {
          dealerVehicles?.length
          ? <ResultsVehicle
              showHeadline={location.pathname.includes(Route.RESULTS)}
              background='001E50'
              color='FFF'
              headline={
                <Text
                  tag={TextTag.h2}
                  appearance={TokenTextAppearance.bigcopy100}
                  color={TokenTextColor.inherit}
                >
                  { replaceStringWithJSX(labels.local_results, { localDealer: <Text bold>{localDealer ?.name}</Text> }) }
                </Text>
              }
              vehicles={dealerVehicles as VehicleData[]}
              secondaryVehicles={regularVehicles as VehicleData[]}
            />
          : null
        }


              {
                regularVehicles?.length
                ? <ResultsVehicle
                    showHeadline={location.pathname.includes(Route.RESULTS)}
                    background='A3D3F3'
                    color='000'
                    headline={
                      <Text tag={TextTag.h2} appearance={TokenTextAppearance.bigcopy100} color={TokenTextColor.inherit}>
                        {
                        replaceStringWithJSX(labels.rest_of_results, {
                          distance: <Text bold>{formatDistance(locationCtx.distance, lang)}</Text>,
                          postalCode: <Text bold>{ locationCtx.postalCode }</Text>
                        })
                        }
                      </Text>
                    }
                    vehicles={regularVehicles as VehicleData[]}
                    secondaryVehicles={dealerVehicles as VehicleData[]}
                  />
                : null
              }

          {/* {
            vehicleStore.response && !vehicleStore.response?.meta?.pagination?.total
            ? <NoResultsVehicle />
            : null
          } */}
			</VehicleResultsContainer>
		);
};

